import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useMountGuideRegion } from '../../hooks/useMountGuideData';
import { guideText } from '../../utils/guideLocale';
import { resolvePublicMediaUrl } from '../../utils/newsContent';
import '../../styles/MountGuide.css';

const MountGuideRegionPage: React.FC = () => {
    const { regionSlug } = useParams<{ regionSlug: string }>();
    const { locale } = useLanguage();

    const labels =
        locale === 'pt-BR'
            ? {
                  guide: 'Guia do jogo',
                  mounts: 'Montarias',
                  notFound: 'Região não encontrada.',
                  back: 'Todas as regiões',
              }
            : {
                  guide: 'Game guide',
                  mounts: 'Mounts',
                  notFound: 'Region not found.',
                  back: 'All regions',
              };

    const { data: region, loading, error } = useMountGuideRegion(regionSlug, labels.notFound);
    const showLoading = loading && !region;

    if (showLoading) {
        return <p className="game-sidebar-muted">{locale === 'pt-BR' ? 'Carregando…' : 'Loading…'}</p>;
    }

    if (!region || error) {
        return (
            <div className="game-main-inner">
                <p>{error ?? labels.notFound}</p>
                <Link to="/game/montarias" className="btn btn-outline btn-sm">
                    {labels.back}
                </Link>
            </div>
        );
    }

    const title = guideText(locale, region.titleEn, region.titlePtBr);
    const banner = resolvePublicMediaUrl(region.bannerUrl);

    return (
        <div className="game-main-inner mount-guide-page">
            <nav className="mount-guide-breadcrumb">
                <Link to="/game">{labels.guide}</Link>
                <span>›</span>
                <Link to="/game/montarias">{locale === 'pt-BR' ? 'Índice' : 'Index'}</Link>
                <span>›</span>
                <span>{title}</span>
            </nav>

            <h1 className="mount-guide-region-title">{title}</h1>

            {banner && <img className="mount-guide-region-banner" src={banner} alt={title} />}

            {region.rarityGroups.length === 0 && (
                <p className="mount-guide-empty">
                    {locale === 'pt-BR' ? 'Nenhuma montaria nesta região.' : 'No mounts in this region.'}
                </p>
            )}

            {region.rarityGroups.map((group) => (
                <section
                    key={group.id}
                    className="mount-guide-rarity-block"
                    style={{ ['--rarity-accent' as string]: group.accentColor }}
                >
                    <h2 className="mount-guide-rarity-title">
                        {guideText(locale, group.titleEn, group.titlePtBr)}
                    </h2>
                    <ul className="mount-guide-mount-grid">
                        {group.mounts.map((mount) => {
                            const mountTitle = guideText(locale, mount.titleEn, mount.titlePtBr);
                            const icon = resolvePublicMediaUrl(mount.iconUrl);
                            return (
                                <li key={mount.id}>
                                    <Link
                                        to={`/game/montarias/${region.slug}/${mount.slug}`}
                                        className="mount-guide-mount-item"
                                    >
                                        <span className="mount-guide-mount-icon">
                                            {icon ? (
                                                <img src={icon} alt="" />
                                            ) : (
                                                <i className="ri-bear-smile-line" style={{ fontSize: 28, padding: 12 }} />
                                            )}
                                        </span>
                                        <span className="mount-guide-mount-name">{mountTitle}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}

            <button type="button" className="mount-guide-top-btn" onClick={() => window.scrollTo({ top: 0 })}>
                TOP
            </button>

            <p>
                <Link to="/game/montarias" className="btn btn-outline btn-sm">
                    <i className="ri-arrow-left-line" /> {labels.back}
                </Link>
            </p>
        </div>
    );
};

export default MountGuideRegionPage;
